import {
  Node,
  NodeDimensionChange,
  NodePositionChange,
  XYPosition,
} from '@xyflow/react';

export interface HelperLinesConfig {
  /** Node types to use for helper lines (defaults to ['tile']) */
  nodeTypes: string[];
  /** Horizontal alignment options (Y-axis) */
  horizontal: {
    /** Align top edges */
    top: boolean;
    /** Align vertical centers (horizontal midpoint) */
    center: boolean;
    /** Align bottom edges */
    bottom: boolean;
  };
  /** Vertical alignment options (X-axis) */
  vertical: {
    /** Align left edges */
    left: boolean;
    /** Align right edges */
    right: boolean;
    /** Align horizontal centers (vertical midpoint) */
    center: boolean;
  };
}

export const DEFAULT_HELPER_LINES_CONFIG: HelperLinesConfig = {
  nodeTypes: ['tile'],
  horizontal: {
    top: true,
    center: true,
    bottom: true,
  },
  vertical: {
    left: true,
    right: true,
    center: true,
  },
};

export interface GetHelperLinesResult {
  horizontal?: number;
  vertical?: number;
  snapPosition: Partial<XYPosition>;
  snapDimensions: { width?: number; height?: number };
}

export interface ResizeHelperLinesOptions {
  snapDistance?: number;
  config?: HelperLinesConfig;
}

export interface HelperLineOption {
  /** Pixel coordinate of the helper line (x for vertical, y for horizontal) */
  line: number;
  /** Distance in pixels between the two candidate edges that produced this option */
  distance: number;
  /** Position that the node should be moved to if this option is chosen */
  snapPosition?: Partial<XYPosition>;
  /** Dimension the node should be snapped to if this option is chosen */
  snapDimensions?: { width?: number; height?: number };
}

export interface ResizeHelperLinesResult {
  /** Candidate horizontal helper lines (Y-axis) */
  horizontal: HelperLineOption[];
  /** Candidate vertical helper lines (X-axis) */
  vertical: HelperLineOption[];
}

interface NodeBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Calculate bounds for a node using its position and dimensions
 */
function calculateNodeBounds(
  node: Node,
  dimensions?: { width?: number; height?: number }
): NodeBounds {
  const width = dimensions?.width ?? node.measured?.width ?? node.width ?? 0;
  const height =
    dimensions?.height ?? node.measured?.height ?? node.height ?? 0;

  return {
    left: node.position.x,
    right: node.position.x + width,
    top: node.position.y,
    bottom: node.position.y + height,
    width,
    height,
  };
}

/**
 * Generate vertical alignment options between two node bounds
 */
function getVerticalAlignmentOptions(
  nodeABounds: NodeBounds,
  nodeBBounds: NodeBounds,
  config: HelperLinesConfig = DEFAULT_HELPER_LINES_CONFIG
): HelperLineOption[] {
  const options: HelperLineOption[] = [];
  const centerA = nodeABounds.left + nodeABounds.width / 2;
  const centerB = nodeBBounds.left + nodeBBounds.width / 2;

  // Left edge alignments
  if (config.vertical?.left !== false) {
    options.push(
      // Left edge to left edge alignment
      {
        line: nodeBBounds.left,
        distance: Math.abs(nodeABounds.left - nodeBBounds.left),
        snapPosition: { x: nodeBBounds.left },
      },
      // Left edge to right edge alignment
      {
        line: nodeBBounds.right,
        distance: Math.abs(nodeABounds.left - nodeBBounds.right),
        snapPosition: { x: nodeBBounds.right },
      }
    );
  }

  // Right edge alignments
  if (config.vertical?.right !== false) {
    options.push(
      // Right edge to right edge alignment
      {
        line: nodeBBounds.right,
        distance: Math.abs(nodeABounds.right - nodeBBounds.right),
        snapDimensions: { width: nodeBBounds.right - nodeABounds.left },
      },
      // Right edge to left edge alignment
      {
        line: nodeBBounds.left,
        distance: Math.abs(nodeABounds.right - nodeBBounds.left),
        snapDimensions: { width: nodeBBounds.left - nodeABounds.left },
      }
    );
  }

  // Center to center alignment (vertical)
  if (config.vertical?.center !== false) {
    options.push({
      line: centerB,
      distance: Math.abs(centerA - centerB),
      snapPosition: { x: centerB - nodeABounds.width / 2 },
    });
  }

  return options;
}

/**
 * Generate horizontal alignment options between two node bounds
 */
function getHorizontalAlignmentOptions(
  nodeABounds: NodeBounds,
  nodeBBounds: NodeBounds,
  config: HelperLinesConfig = DEFAULT_HELPER_LINES_CONFIG
): HelperLineOption[] {
  const options: HelperLineOption[] = [];
  const centerA = nodeABounds.top + nodeABounds.height / 2;
  const centerB = nodeBBounds.top + nodeBBounds.height / 2;

  // Top edge to top edge alignment
  if (config.horizontal?.top !== false) {
    options.push({
      line: nodeBBounds.top,
      distance: Math.abs(nodeABounds.top - nodeBBounds.top),
      snapPosition: { y: nodeBBounds.top },
    });
  }

  // Center to center alignment (horizontal)
  if (config.horizontal?.center !== false) {
    options.push({
      line: centerB,
      distance: Math.abs(centerA - centerB),
      snapPosition: { y: centerB - nodeABounds.height / 2 },
    });
  }

  // Bottom edge alignments
  if (config.horizontal?.bottom !== false) {
    options.push(
      // Bottom edge to bottom edge alignment
      {
        line: nodeBBounds.bottom,
        distance: Math.abs(nodeABounds.bottom - nodeBBounds.bottom),
        snapDimensions: { height: nodeBBounds.bottom - nodeABounds.top },
      },
      // Top edge to bottom edge alignment
      {
        line: nodeBBounds.bottom,
        distance: Math.abs(nodeABounds.top - nodeBBounds.bottom),
        snapPosition: { y: nodeBBounds.bottom },
      },
      // Bottom edge to top edge alignment
      {
        line: nodeBBounds.top,
        distance: Math.abs(nodeABounds.bottom - nodeBBounds.top),
        snapDimensions: { height: nodeBBounds.top - nodeABounds.top },
      }
    );
  }

  return options;
}

/**
 * Filter and sort alignment options by distance within snap threshold
 */
function filterAndSortAlignmentOptions(
  options: HelperLineOption[],
  snapDistance: number
): HelperLineOption[] {
  return options
    .filter((option) => option.distance < snapDistance)
    .sort((a, b) => a.distance - b.distance);
}

/**
 * Calculate helper lines for resize operations with improved readability
 */
export function getHelperLinesForResize(
  dimensionChange: NodeDimensionChange,
  nodes: Node[],
  options: ResizeHelperLinesOptions = {}
): ResizeHelperLinesResult {
  const { snapDistance = 5, config = DEFAULT_HELPER_LINES_CONFIG } = options;

  // Find the node being resized
  const resizingNode = nodes.find((node) => node.id === dimensionChange.id);
  if (!resizingNode || !dimensionChange.dimensions) {
    return { horizontal: [], vertical: [] };
  }

  // Calculate bounds for the resizing node
  const resizingNodeBounds = calculateNodeBounds(
    resizingNode,
    dimensionChange.dimensions
  );

  // Collect alignment options from all other nodes
  const verticalOptions: HelperLineOption[] = [];
  const horizontalOptions: HelperLineOption[] = [];

  const otherNodes = nodes.filter((node) => node.id !== resizingNode.id);

  for (const otherNode of otherNodes) {
    const otherNodeBounds = calculateNodeBounds(otherNode);

    verticalOptions.push(
      ...getVerticalAlignmentOptions(
        resizingNodeBounds,
        otherNodeBounds,
        config
      )
    );
    horizontalOptions.push(
      ...getHorizontalAlignmentOptions(
        resizingNodeBounds,
        otherNodeBounds,
        config
      )
    );
  }

  return {
    horizontal: filterAndSortAlignmentOptions(horizontalOptions, snapDistance),
    vertical: filterAndSortAlignmentOptions(verticalOptions, snapDistance),
  };
}

/**
 * @description Checks all other nodes and calculates the helper line positions and the position where the current node should snap to
 *
 * A lot of this is adapted from: https://reactflow.dev/docs/examples/interaction/helper-lines/,
 * BUT, note that we have added some of our own logic as well (around center node lines in particular)
 */
export function getHelperLines(
  change: NodePositionChange,
  nodes: Node[],
  snapDistance = 5,
  config: HelperLinesConfig = DEFAULT_HELPER_LINES_CONFIG
): GetHelperLinesResult {
  const defaultResult = {
    horizontal: undefined,
    vertical: undefined,
    snapPosition: { x: undefined, y: undefined },
    snapDimensions: { width: undefined, height: undefined },
  };

  const nodeA = nodes.find((node) => node.id === change.id);
  if (!nodeA || !change.position) return defaultResult;

  const nodeAWidth = nodeA.measured?.width ?? nodeA.width ?? 0;
  const nodeAHeight = nodeA.measured?.height ?? nodeA.height ?? 0;

  const nodeABounds = {
    left: change.position.x,
    right: change.position.x + nodeAWidth,
    top: change.position.y,
    bottom: change.position.y + nodeAHeight,
    width: nodeAWidth,
    height: nodeAHeight,
  };

  let horizontalDistance = snapDistance;
  let verticalDistance = snapDistance;

  return nodes
    .filter((node) => node.id !== nodeA.id)
    .reduce<GetHelperLinesResult>((result, nodeB) => {
      const nodeBWidth = nodeB.measured?.width ?? nodeB.width ?? 0;
      const nodeBHeight = nodeB.measured?.height ?? nodeB.height ?? 0;

      const nodeBBounds = {
        left: nodeB.position.x,
        right: nodeB.position.x + nodeBWidth,
        top: nodeB.position.y,
        bottom: nodeB.position.y + nodeBHeight,
        width: nodeBWidth,
        height: nodeBHeight,
      };

      // Left edge alignments
      if (config.vertical?.left !== false) {
        //  |‾‾‾‾‾‾‾‾‾‾‾|
        //  |     A     |
        //  |___________|
        //  |
        //  |
        //  |‾‾‾‾‾‾‾‾‾‾‾|
        //  |     B     |
        //  |___________|
        const distanceLeftLeft = Math.abs(nodeABounds.left - nodeBBounds.left);

        if (distanceLeftLeft < verticalDistance) {
          result.snapPosition.x = nodeBBounds.left;
          result.vertical = nodeBBounds.left;
          verticalDistance = distanceLeftLeft;
        }

        //              |‾‾‾‾‾‾‾‾‾‾‾|
        //              |     A     |
        //              |___________|
        //              |
        //              |
        //  |‾‾‾‾‾‾‾‾‾‾‾|
        //  |     B     |
        //  |___________|
        const distanceLeftRight = Math.abs(
          nodeABounds.left - nodeBBounds.right
        );

        if (distanceLeftRight < verticalDistance) {
          result.snapPosition.x = nodeBBounds.right;
          result.vertical = nodeBBounds.right;
          verticalDistance = distanceLeftRight;
        }
      }

      // Right edge alignments
      if (config.vertical?.right !== false) {
        //  |‾‾‾‾‾‾‾‾‾‾‾|
        //  |     A     |
        //  |___________|
        //              |
        //              |
        //  |‾‾‾‾‾‾‾‾‾‾‾|
        //  |     B     |
        //  |___________|
        const distanceRightRight = Math.abs(
          nodeABounds.right - nodeBBounds.right
        );

        if (distanceRightRight < verticalDistance) {
          result.snapPosition.x = nodeBBounds.right - nodeABounds.width;
          result.vertical = nodeBBounds.right;
          verticalDistance = distanceRightRight;
        }

        //  |‾‾‾‾‾‾‾‾‾‾‾|
        //  |     A     |
        //  |___________|
        //              |
        //              |
        //              |‾‾‾‾‾‾‾‾‾‾‾|
        //              |     B     |
        //              |___________|
        const distanceRightLeft = Math.abs(
          nodeABounds.right - nodeBBounds.left
        );

        if (distanceRightLeft < verticalDistance) {
          result.snapPosition.x = nodeBBounds.left - nodeABounds.width;
          result.vertical = nodeBBounds.left;
          verticalDistance = distanceRightLeft;
        }
      }

      // Top edge alignment
      if (config.horizontal?.top !== false) {
        //  |‾‾‾‾‾‾‾‾‾‾‾|‾‾‾‾‾|‾‾‾‾‾‾‾‾‾‾‾|
        //  |     A     |     |     B     |
        //  |___________|     |___________|
        const distanceTopTop = Math.abs(nodeABounds.top - nodeBBounds.top);

        if (distanceTopTop < horizontalDistance) {
          result.snapPosition.y = nodeBBounds.top;
          result.horizontal = nodeBBounds.top;
          horizontalDistance = distanceTopTop;
        }
      }

      // Horizontal center alignment
      if (config.horizontal?.center !== false) {
        //  |‾‾‾‾‾‾‾‾‾‾‾|     |‾‾‾‾‾‾‾‾‾‾‾|
        //  |     A     |‾‾‾‾‾|     B     |
        //  |___________|     |___________|
        const midPointAHorizontal = nodeABounds.top + nodeABounds.height / 2;
        const midPointBHorizontal = nodeBBounds.top + nodeBBounds.height / 2;
        const distanceMiddleMiddleHorizontal = Math.abs(
          midPointAHorizontal - midPointBHorizontal
        );

        if (distanceMiddleMiddleHorizontal < horizontalDistance) {
          result.snapPosition.y = midPointBHorizontal - nodeABounds.height / 2;
          result.horizontal = midPointBHorizontal;
          horizontalDistance = distanceMiddleMiddleHorizontal;
        }
      }

      // Vertical center alignment
      if (config.vertical?.center !== false) {
        //        |‾‾‾‾‾‾‾‾‾‾‾|
        //        |     B     |
        //        |___________|
        //             |
        //             |
        //        |‾‾‾‾‾‾‾‾‾‾‾|
        //        |     A     |
        //        |___________|
        const midPointAVertical = nodeABounds.left + nodeABounds.width / 2;
        const midPointBVertical = nodeBBounds.left + nodeBBounds.width / 2;
        const distanceMiddleMiddleVertical = Math.abs(
          midPointAVertical - midPointBVertical
        );

        if (distanceMiddleMiddleVertical < verticalDistance) {
          result.snapPosition.x = midPointBVertical - nodeABounds.width / 2;
          result.vertical = midPointBVertical;
          verticalDistance = distanceMiddleMiddleVertical;
        }
      }

      return result;
    }, defaultResult);
}
